import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Trash2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_COLORS = {
  'ErrorBoundary':      { bg: 'bg-red-100',    text: 'text-red-700',    badge: 'bg-red-200'    },
  'window.onerror':     { bg: 'bg-orange-100', text: 'text-orange-700', badge: 'bg-orange-200' },
  'unhandledrejection': { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-200' },
  'console.error':      { bg: 'bg-gray-100',   text: 'text-gray-700',   badge: 'bg-gray-200'   },
};
const defaultColors = { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100' };

function ErrorRow({ error }) {
  const [expanded, setExpanded] = useState(false);
  const colors = TYPE_COLORS[error.type] || defaultColors;

  return (
    <div className={`rounded-lg border border-gray-200 overflow-hidden mb-2`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors`}
      >
        <span className="mt-0.5 text-gray-400 flex-shrink-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge} ${colors.text}`}>
              {error.type}
            </span>
            <span className="text-xs text-gray-400">
              {new Date(error.created_at).toLocaleString('de-DE')}
            </span>
          </div>
          <p className="text-sm text-gray-800 mt-1 truncate">{error.message}</p>
          {error.url && (
            <p className="text-xs text-gray-400 truncate">{error.url}</p>
          )}
        </div>
      </button>

      {expanded && (
        <div className={`px-4 pb-4 space-y-2 ${colors.bg} border-t border-gray-200`}>
          <Detail label="Typ"      value={error.type} />
          <Detail label="Nachricht" value={error.message} />
          <Detail label="URL"      value={error.url} />
          <Detail label="IP"       value={error.ip} />
          <Detail label="Browser"  value={error.user_agent} />
          <Detail label="Zeit"     value={new Date(error.created_at).toLocaleString('de-DE')} />
          {error.stack && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Stack Trace</p>
              <pre className="text-xs bg-white rounded p-3 overflow-x-auto whitespace-pre-wrap break-all border border-gray-200 max-h-60">
                {error.stack}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <span className="text-xs font-semibold text-gray-500">{label}: </span>
      <span className="text-xs text-gray-700 break-all">{value}</span>
    </div>
  );
}

const ErrorLogTab = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/frontend-errors?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setErrors(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error loading frontend errors:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    if (!window.confirm('Alle Fehler löschen?')) return;
    const token = localStorage.getItem('token');
    await fetch(`${API_URL}/admin/frontend-errors`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setErrors([]);
  };

  const types = ['all', ...Array.from(new Set(errors.map(e => e.type)))];
  const filtered = filter === 'all' ? errors : errors.filter(e => e.type === filter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <h2 className="text-xl font-semibold">Frontend Fehler-Log</h2>
          <span className="text-sm text-gray-400">({filtered.length} Einträge)</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" /> Aktualisieren
          </button>
          <button
            onClick={clearAll}
            disabled={errors.length === 0}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" /> Alle löschen
          </button>
        </div>
      </div>

      {/* Type filter */}
      <div className="flex gap-2 flex-wrap">
        {types.map(t => {
          const colors = TYPE_COLORS[t] || defaultColors;
          return (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                filter === t
                  ? `${colors.badge} ${colors.text} border-transparent`
                  : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
              }`}
            >
              {t === 'all' ? 'Alle' : t}
              {t !== 'all' && (
                <span className="ml-1 opacity-60">
                  ({errors.filter(e => e.type === t).length})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>Keine Fehler vorhanden</p>
        </div>
      ) : (
        <div>{filtered.map(e => <ErrorRow key={e.id} error={e} />)}</div>
      )}
    </div>
  );
};

export default ErrorLogTab;
